import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const RelatedLocations = ({ currentLocation }) => {
  const { language } = useLanguage();
  
  const getLocalizedPath = (path) => {
    return language === 'ru' ? `/ru${path}` : path;
  };

  const popularLocations = [
    { 
      slug: 'vps-netherlands', 
      nameEn: 'Netherlands', 
      nameRu: 'Нидерланды', 
      city: 'Meppel',
      flag: '/images/locations/flag-nl.png'
    },
    { 
      slug: 'vps-germany', 
      nameEn: 'Germany', 
      nameRu: 'Германия', 
      city: 'Dusseldorf',
      flag: '/images/locations/flag-de.png'
    },
    { 
      slug: 'vps-poland', 
      nameEn: 'Poland', 
      nameRu: 'Польша', 
      city: 'Gdansk',
      flag: '/images/locations/flag-pl.png'
    },
    { 
      slug: 'vps-switzerland', 
      nameEn: 'Switzerland', 
      nameRu: 'Швейцария', 
      city: 'Geneva',
      flag: '/images/locations/flag-ch.png'
    },
    { 
      slug: 'vps-usa', 
      nameEn: 'USA', 
      nameRu: 'США', 
      city: 'Los Angeles',
      flag: '/images/locations/flag-us.png'
    },
    { 
      slug: 'vps-miami', 
      nameEn: 'USA, Miami', 
      nameRu: 'США, Майами', 
      city: 'Miami, FL',
      flag: '/images/locations/flag-us.png'
    },
    { 
      slug: 'vps-czechia', 
      nameEn: 'Czech Republic', 
      nameRu: 'Чехия', 
      city: 'Prague',
      flag: '/images/locations/flag-cz.png'
    },
    {
      slug: 'vps-bulgaria',
      nameEn: 'Bulgaria',
      nameRu: 'Болгария',
      city: 'Sofia',
      flag: '/flags/bg.svg'
    },
    {
      slug: 'vps-greece',
      nameEn: 'Greece',
      nameRu: 'Греция',
      city: 'Thessaloniki',
      flag: '/flags/gr.svg'
    },
    {
      slug: 'vps-spain',
      nameEn: 'Spain',
      nameRu: 'Испания',
      city: 'Barcelona',
      flag: '/flags/es.svg'
    },
      {
      slug: 'vps-romania',
      nameEn: 'Romania',
      nameRu: 'Румыния',
      city: 'Bucharest',
      flag: '/flags/ro.svg'
    },
];

  // Deterministic rotation by currentLocation hash so every page shows a
  // *different* set of 4 — across the 12 location pages this means every
  // country gets internal links from somewhere (search-engine friendly),
  // and customers landing on Netherlands don't always see the same four
  // "top" siblings.
  const _hash = (s) => {
    let h = 0;
    for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) & 0xffffffff;
    return Math.abs(h);
  };
  const _others = popularLocations.filter(loc => loc.slug !== currentLocation);
  const _offset = _others.length ? (_hash(currentLocation) % _others.length) : 0;
  const displayLocations = [..._others.slice(_offset), ..._others.slice(0, _offset)].slice(0, 4);

  return (
    <section className="py-12 bg-gradient-to-br from-blue-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            {language === 'en' ? 'Popular VPS Locations' : 'Популярные локации VPS'}
          </h2>
          <p className="text-lg text-gray-600">
            {language === 'en' 
              ? 'Explore our VPS hosting in the most popular locations'
              : 'Исследуйте наш VPS хостинг в самых популярных локациях'}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayLocations.map((location) => (
            <Link
              key={location.slug}
              to={getLocalizedPath(`/${location.slug}`)}
              className="group bg-white rounded-xl p-6 transition-all duration-300 border-2 border-gray-200 hover:border-blue-500 cursor-pointer"
            >
              <div className="flex items-center gap-3 mb-3">
                <img 
                  src={location.flag} 
                  alt={`${language === 'en' ? location.nameEn : location.nameRu} flag`}
                  loading="lazy"
                  className="w-10 h-7 object-cover rounded-md"
                />
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                  {language === 'en' ? location.nameEn : location.nameRu}
                </h3>
              </div>
              <div className="flex items-center gap-1 text-gray-600 mb-3">
                <MapPin className="w-4 h-4" />
                <p className="text-sm">{location.city}</p>
              </div>
              <div className="mt-4 text-sm text-blue-600 font-semibold">
                {language === 'en' ? 'Learn more →' : 'Узнать больше →'}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
